import { CreateDocumentBody, CreateSessionBody, DocumentDto, OTPBody, SectionDto, SessionDto, SessionsQueryRestrictions, SignatureDto, UpdateDocumentInfos, UpdateSessionBody, UpdateSessionContactBody, UpdateSignatureBody } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import {  SessionStatus, SessionSystemType, SignatureStatus } from "@bcasmart/doxecure-commons/doxecureModelTypes";
import { DocumentRepresentation, SectionCategory, SessionActorType, SignatureMode } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $count, $isarray, $isuuid, $json, $ok, $tounsigned } from "foundation-ts/commons";
import { $dateorder } from "foundation-ts/compare";
import { TSDate } from "foundation-ts/tsdate";
import { TSError } from "foundation-ts/tserrors";
import { Resp, Verb } from "foundation-ts/tsrequest";
import { Comparison, Nullable, UUID } from "foundation-ts/types";
import { $inspect } from "foundation-ts/utils";

import apiClient, { assertResponse } from "@services/apiClient";
import { fileNameString, optlog } from "@utils/functions";

export const createSessionForUser = async (session: SessionDto, userID:Nullable<UUID>): Promise<SessionDto> => {
    if ($ok(session.apid)) { throw "createSessionForUser(): session.apid should NOT BE SET" ; }
    if (!$ok(userID)) { throw `createSessionForUser() cannot have a valid user's profileID` ; }

    const body: CreateSessionBody = {
        creator: userID!,
        start: session.validity.start,
        end: session.validity.end,
        title: session.title,
    } ;
    if ($ok(session.description) && (session.description!.length > 0)) { body.description = session.description ; }
    if (session.systemType === SessionSystemType.OneToMany) { body.withSubSession = true; }
    if ($ok(session.expeditor) && (session.expeditor.apid !== userID!)) { body.expeditor = session.expeditor.apid ; }
    if ($isuuid(session.templateId)) { body.templateId = session.templateId ; }
    _addSignaturesToBody(body, session.signatures, true) ;
    optlog(`Added ${$count(body.signatures)} signatures to future new session`) ;
    _addDocumentsToBody(body, session.documents, true) ;
    optlog(`Added ${$count(body.documents)} documents to future new session`) ;
    if ($count(session.approvals) !== 0) {
        body.approvers = session.approvals!.map(a => a.approver.apid!);
        optlog(`Added ${$count(body.approvers)} approvers to future new session`) ;
    }
    if ($count(session.recipients) !== 0) {
        body.recipients = session.recipients!.map(u => u.apid!);
        optlog(`Added ${$count(body.recipients)} recipients for future new session`) ;
    }
    optlog($inspect(body)) ;
    return apiClient.createSession(body)
                    .then(([createdSession, status]) => assertResponse(createdSession, status)) ;
};

export const patchSessionForUser = async (session: SessionDto): Promise<SessionDto> => {
    if (!$isuuid(session.apid)) { throw "patchSessionForUser(): session.apid should be a valid UUID" ; }
    const body: UpdateSessionBody = {
        creator: session.creator,
        start: session.validity.start,
        end: session.validity.end,
        expeditor: session.expeditor.apid,
        title: session.title,
    } ;
    if ($ok(session.description) && (session.description!.length > 0)) { body.description = session.description ; }
    if (session.systemType === SessionSystemType.OneToMany) { body.withSubSession = true; }
    _addSignaturesToBody(body, session.signatures, false) ;
    optlog(`Added ${$count(body.signatures)} signatures for session ${session.apid} update`) ;
    _addDocumentsToBody(body, session.documents, false) ;
    optlog(`Added ${$count(body.documents)} documents for session ${session.apid} update`) ;
    if ($count(session.approvals) !== 0) {
        body.approvers = session.approvals!.map(a => a.approver.apid!);
        optlog(`Added ${$count(body.approvers)} approvers for session ${session.apid} update`) ;
    }
    if ($count(session.recipients) !== 0) {
        body.recipients = session.recipients!.map(u => u.apid!);
        optlog(`Added ${$count(body.recipients)} recipients for session ${session.apid} update`) ;
    }
    optlog($inspect(body)) ;
    return apiClient.updateSession(session.apid!, body)
                    .then(([updatedSession, status]) => assertResponse(updatedSession, status)) ;
};

export const getSectionsForUser = async (
    userID: Nullable<UUID>,
    roles: SessionActorType|SessionActorType[],
    restrictions: SessionsQueryRestrictions = SessionsQueryRestrictions.None,
    category?: SectionCategory,
    fullTextSearch?: string,
    nocookie?: boolean): Promise<SectionDto[]> => {
    
    optlog(`getSectionsForUser(${userID}, ${roles}, ${restrictions}, ${category}, ${fullTextSearch})`) ;

    if(!$ok(userID)) { TSError.throw(`getSectionsForUser() needs profileId`); }

    if ($isarray(roles) && $count(roles as SessionActorType[]) === 1) {
        roles = (roles as SessionActorType[])[0] ;
    }

    if ($isarray(roles)) {
        const knownSessions = new Set<string>() ;
        const base = new Map<SectionCategory, SessionDto[]>() ;
        for (let r of roles as SessionActorType[]) {
            const [resp, status] = await apiClient.getSections({
                role:r,
                user: userID!,
                status: category,
                restrictions: restrictions,
                textSearch: fullTextSearch
            }, nocookie) ;
            
            if (!$ok(resp)) { TSError.throw(`getSectionsForUser() for role ${r} did return status ${status}`, status) ; }

            for (let section of resp!) {
                let list = base.get(section.category) ;
                let addlist = false ;
                if (!$ok(list)) { list = [] ; addlist = true ; }

                for (let session of section.sessions) {
                    if ($ok(session.apid) && !knownSessions.has(session.apid!)) {
                        knownSessions.add(session.apid!) ;
                        list!.push(session) ;
                    }
                }
                if (addlist && list?.length) { base.set(section.category, list) ; } // add only if we have sessions in our list
            }
        }

        // here, since sessions can come from different roles, we need to sort them
        const ret:SectionDto[] = [] ;
        base.forEach((sessions, category) => {
            if ((category === 'signed')) {
                ret.push({category:category, sessions:sessions.sort(_compareSignedSessionDto)}) ;
            } else if (category === 'refused') {
                ret.push({category:category, sessions:sessions.sort(_compareRefusedSessionDto)}) ;
            } else if (category === 'canceled') {
                ret.push({category:category, sessions:sessions.sort(_compareCanceledSessionDto)})
            } else if ((category === 'archived') || (category === 'draft')) {
                ret.push({category:category, sessions:sessions.sort(_compareDescendingSessionDto)}) ;
            } else if (category === 'expired') {
                ret.push({category:category, sessions:sessions.sort(_compareExpiredSessionDto)}) ;
            } else {
                ret.push({category:category, sessions:sessions.sort(_compareSessionDto)}) ;
            }
        }) ;
        return ret ;
    }

    // here each section's session is sorted in the backend. No need to sort it again
    // except if you want to change the way they are sorted
    return apiClient.getSections({
        role: roles as SessionActorType,
        user: userID!,
        status: category,
        restrictions: restrictions,
        textSearch: fullTextSearch
    }, nocookie).then(([sections, status]) => assertResponse(sections, status)) ;
};

export const createTemplateSessionForUser = async (session: SessionDto, userId: Nullable<UUID>): Promise<SessionDto> => {
    if ($ok(session.apid)) { throw "createTemplateSessionForUser(): session.apid should NOT BE SET" ; }
    if (!$ok(userId)) { throw `createTemplateSessionForUser() cannot have a valid user's profileID` ; }

    const body: CreateSessionBody = {
        creator: userId!,
        start: session.validity.start,
        end: session.validity.end,
        title: session.title,
        expeditor: session.expeditor.apid!
    } ;
    if ($ok(session.description) && (session.description!.length > 0)) { body.description = session.description ; }
    if (session.systemType === SessionSystemType.OneToManyTemplate) { body.withSubSession = true; }
    _addSignaturesToBody(body, session.signatures, true) ;
    optlog(`Added ${$count(body.signatures)} signatures to template session`) ;
    if ($ok(session.approvals)) {
        body.approvers = session.approvals!.map(a => a.approver.apid!);
        optlog(`Added ${$count(body.approvers)} approvers to template session`) ;
    }
    if ($count(session.recipients) !== 0) {
        body.recipients = session.recipients!.map(u => u.apid!);
        optlog(`Added ${$count(body.recipients)} recipients to template session`);
    }
    return apiClient.createTemplateSession(body)
                    .then(([createdSession, status]) => assertResponse(createdSession, status)) ;
} ;

export const patchTemplateSessionForUser = async (session: SessionDto): Promise<SessionDto> => {
    if (!$isuuid(session.apid)) { throw "patchTemplateSessionForUser(): session.apid should be a valid UUID" ; }

    const body: UpdateSessionBody = {
        expeditor: session.expeditor.apid,
        start: session.validity.start,
        end: session.validity.end,
        title: session.title,
    } ;
    if ($ok(session.description) && (session.description!.length > 0)) { body.description = session.description ; }
    if (session.systemType === SessionSystemType.OneToManyTemplate) { body.withSubSession = true; }
    _addSignaturesToBody(body, session.signatures, false) ;
    optlog(`Added ${$count(body.signatures)} signatures to template session ${session.apid} update`) ;
    if ($ok(session.approvals)) {
        body.approvers = session.approvals!.map(a => a.approver.apid!);
        optlog(`Added ${$count(body.approvers)} approvers to template session ${session.apid} update`) ;
    }
    if ($count(session.recipients) !== 0) {
        body.recipients = session.recipients!.map(u => u.apid!);
        optlog(`Added ${$count(body.recipients)} recipients to template session ${session.apid} update`) ;
    }
    return apiClient.patchTemplateSession(session.apid!, body).then(([ret, status]) => assertResponse(ret, status) ) ;
}

export const getTemplateSessions = async (): Promise<SessionDto[]> => {
    return await apiClient.listTemplateSessions().then(([sessions, status]) => assertResponse(sessions, status)) ;
}

export const deleteTemplateSession = async (sessionId: UUID): Promise<boolean> => {
    return await apiClient.deleteTemplateSession(sessionId) ;
}

export const getTokenSessionStruct = async (token: string) => {
    return await apiClient.getTokenSessionStruct(token) ;
}

export const getOneSessionForUser = async (sessionId: Nullable<UUID>, template?: boolean, token?: Nullable<string>): Promise<SessionDto> => {
    if (!$isuuid(sessionId)) { throw "getOneSessionForUser(): sessionId should be a valid UUID"; }
    return !!template ? apiClient.getTemplateSession(sessionId!).then(([session, status]) => assertResponse(session, status)) : apiClient.getSession(sessionId!, token).then(([session, status]) => assertResponse(session, status));
}

export const deleteOneSessionForUser = async (sessionId: Nullable<UUID>, token?: Nullable<string>): Promise<UUID> => {
    if (!$isuuid(sessionId)) { throw "deleteOneSessionForUser(): sessionId should be a valid UUID"; }
    return apiClient.deleteSession(sessionId!, token).then(([deletedSessionId, status]) => assertResponse(deletedSessionId, status)) ;
};

export const publishSession = async (sessionId: UUID | undefined | null): Promise<boolean> => {
    if (!$isuuid(sessionId)) { throw "publishSession(): sessionId should be a valid UUID"; }
    return apiClient.publishSession(sessionId!).then(([flag, status]) => assertResponse(flag, status)) ;
}

export const abortSession = async (sessionId: UUID | undefined | null): Promise<boolean> => {
    if (!$isuuid(sessionId)) { throw "abortSession(): sessionId should be a valid UUID"; }
    return apiClient.abortSession(sessionId!).then(([flag, status]) => assertResponse(flag, status)) ;
}

export const approveSession = async (sessionId: Nullable<UUID>): Promise<boolean> => {
    if (!$isuuid(sessionId)) { throw "approveSession(): sessionId should be a valid UUID"; }
    return apiClient.approveSession(sessionId!).then(([flag, status]) => assertResponse(flag, status));
}

export const refuseSession = async (sessionId: Nullable<UUID>, reason: string): Promise<boolean> => {
    if (!$isuuid(sessionId)) { throw "refuseSession(): sessionId should be a valid UUID"; }

    const body: UpdateSessionBody = { refusalApprovalReason: reason };
    return apiClient.refuseSession(sessionId!, body).then(([flag, status]) => assertResponse(flag, status));
}

export const sendOTP = async (sessionId: Nullable<UUID>, signatureId: Nullable<UUID>, type: "EMAIL" | "SMS", email: string, token?: Nullable<string>): Promise<boolean> => {
    if (!$isuuid(sessionId)) { throw "sendOTP(): sessionId should be a valid UUID"; }
    if (!$isuuid(signatureId)) { throw "sendOTP(): signatureId should be a valid UUID"; }

    const body: OTPBody = { type , email };
    return apiClient.sendOTP(sessionId!, signatureId!, body, token).then(([flag, status]) => assertResponse(flag, status));
}

export const signDocuments = async (sessionId: Nullable<UUID>, signatureId: Nullable<UUID>, signerId: Nullable<UUID>, otp: string, token?: Nullable<string>): Promise<boolean> => {
    if (!$isuuid(sessionId)) { throw "signDocuments(): sessionId should be a valid UUID"; }
    if (!$isuuid(signatureId)) { throw "signDocuments(): signatureId should be a valid UUID"; }

    const [flag, status] = await apiClient.signDocuments(sessionId!, signatureId!, signerId!, TSDate.zulu(), otp, token);
    if (status !== Resp.OK) { throw new Error(status.toString()); }
    return flag;
}

export const refuseSignature = async (sessionId: Nullable<UUID>, signatureId: Nullable<UUID>, signerId: Nullable<UUID>, date: TSDate, comment?: string, token?: Nullable<string>) => {
    if (!$isuuid(sessionId)) { throw "refuseSignature(): sessionId should be a valid UUID"; }
    if (!$isuuid(signatureId)) { throw "refuseSignature(): signatureId should be a valid UUID"; }

    return apiClient.refuseSignature(sessionId!, signatureId!, signerId!, date, comment, token);
}

export const transferSessionForUser = async (sessionId: UUID | undefined | null): Promise<SessionDto> => {
    if (!$isuuid(sessionId)) { throw "transferSessionForUser(): sessionId should be a valid UUID"; }
    return apiClient.request(`/sessions/${sessionId!}/transfer`, Verb.Patch).then(([ret, _status]) => ret as SessionDto) ;
};

export const downloadAllDocuments = async (sessionId: UUID | undefined | null): Promise<any> => {
    if (!$isuuid(sessionId)) { throw "downloadAllDocuments(): sessionId should be a valid UUID"; }
    return apiClient.downloadSignedDocuments(sessionId!).then(([ret, _status]) => ret) ;
};

export const downloadDocument = async (sessionId: UUID | undefined | null, documentId: UUID | undefined | null): Promise<any> => {
    if (!$isuuid(sessionId)) { throw "downloadDocument(): sessionId should be a valid UUID"; }
    if (!$isuuid(documentId)) { throw "downloadDocument(): documentId should be a valid UUID"; }
    return apiClient.downloadDocument(sessionId!, documentId!).then(([ret, _status]) => ret) ;
};

export const getDocumentRepresentation = async (sessionId: Nullable<UUID>, documentId:Nullable<UUID>, token?: Nullable<string>): Promise<DocumentRepresentation> => {
    if (!$isuuid(sessionId)) { throw "getDocumentRepresentation(): sessionId should be a valid UUID"; }
    if (!$isuuid(documentId)) { throw "getDocumentRepresentation(): documentId should be a valid UUID"; }
    return apiClient.documentRepresentation(sessionId!, documentId!, token).then(([representation, status]) => assertResponse(representation, status)) ;
};

export const patchToBeSignedSession = async (sessionId: Nullable<UUID>, newDate: TSDate): Promise<SessionDto> => {
    if (!$isuuid(sessionId)) { throw "patchToBeSignedSession(): sessionId should be a valid UUID"; }
    return apiClient.updateSession(sessionId!, { end: newDate }).then(([session, status]) => assertResponse(session, status));
}

export const unsubscribeDownloadSignedDocReminder = async (sessionId?: UUID, token?: Nullable<string>, signatureId?: UUID) => {
    if (!$isuuid(sessionId)) { throw "unsubscribeDownloadSignedDocReminder(): sessionId should be a valid UUID"; }
    if ($ok(signatureId) && !$isuuid(signatureId)) { throw "unsubscribeDownloadSignedDocReminder(): signatureId should be a valid UUID";}
    return apiClient.unsubscribeDownloadReminder(sessionId!, token, signatureId);
}

export const downloadAuditTrail = async (sessionId: Nullable<UUID>): Promise<any> => {
    if (!$isuuid(sessionId)) { throw "downloadAllDocuments(): sessionId should be a valid UUID"; }
    return apiClient.downloadAuditTrail(sessionId!).then(([ret, _status]) => ret);
}

export const updateSessionContact = async (sessionId: UUID, contactId: UUID, body: UpdateSessionContactBody, token: string) => {
    if (!$isuuid(sessionId)) { throw "updateSessionContact(): sessionId should be a valid UUID"; }
    if (!$isuuid(contactId)) { throw "updateSessionContact(): contactId should be a valid UUID"; }
    return apiClient.updateSessionContact(sessionId, contactId, body, token);
}

// simpler to make body as any here... but that's not good practice
function _addSignaturesToBody(body:any, signatures:SignatureDto[]|undefined|null, creation:boolean) {
    if ((creation && $count(signatures) > 0) || (!creation && $ok(signatures))) {
        body.signatures = signatures!.map(s => {
            const apid = s.signer.apid ;
            const sb:UpdateSignatureBody = {
                signer: $isuuid(apid) ? apid! : { firstName:s.signer.firstName, lastName:s.signer.lastName, email:s.signer.email },
                rank:s.rank,
                type:s.signatureType,
//                phase:s.phase,
                mode:$ok(s.signatureMode) ? s.signatureMode! : SignatureMode.Visible
            } ;
            if (!creation && $ok(s.apid)) { sb.apid = s.apid ; } // for update only
            return sb ;
        }) ;
    }
}

function _addDocumentsToBody(body:any, documents:DocumentDto[]|undefined|null, creation:boolean) {
    if ((creation && $count(documents) > 0) || (!creation && $ok(documents))) {
        body.documents = documents!.map(d => {
            const apid = d.apid ;
            if (apid && !creation) { 
                const ret:UpdateDocumentInfos = { 
                    apid: apid,
                    lastPageSignRows: $tounsigned(d.lastPageSignRows),
                    visibleSignatures: d.visibleSignatures 
                } 
                optlog('Will update document infos',ret.apid, ret.lastPageSignRows) ;
                return ret ;
            }
            else if (!apid && $ok(d.localSource)) {
                const index = d.localSource!.indexOf(',') ;
                const ret:CreateDocumentBody = {
                    fileName:fileNameString(d.fileName),
                    fileType:d.fileType,
                    mimeType:d.mimeType,
                    size: d.size,
                    pagesCount: d.pagesCount,
                    localSource:index === -1 ? d.localSource! : d.localSource!.slice(index+1),
                    lastPageSignRows:$tounsigned(d.lastPageSignRows),
                    visibleSignatures: d.visibleSignatures
                } ;
                optlog('Will add document \n'+$json(ret.fileName)) ;
                return ret ;
            }
            return undefined ;
        }) ;
    }
}

function _compareSessionDto(A: SessionDto, B: SessionDto): NonNullable<Comparison> 
{ 
    return $dateorder(A.validity.end, B.validity.end) ;
}

function _compareDescendingSessionDto(A: SessionDto, B: SessionDto): NonNullable<Comparison> {
    return $dateorder(B.validity.end, A.validity.end) ;
}

function _compareSignedSessionDto(A: SessionDto, B: SessionDto): NonNullable<Comparison> {
    return $dateorder(B.signatures?.last()?.signatureDate, A.signatures?.last()?.signatureDate) ;
}

function _compareRefusedSessionDto(A: SessionDto, B: SessionDto): NonNullable<Comparison> {
    let dateA: Nullable<TSDate> = A.validity.end ;
    let dateB: Nullable<TSDate> = B.validity.end ;
    let refusedSignature = A.signatures?.find(signature => signature.signatureStatus === SignatureStatus.Refused) ;
    if ($ok(refusedSignature)) { dateA = $ok(refusedSignature!.refusalDate) ? TSDate.fromIsoString(refusedSignature!.refusalDate) : refusedSignature!.signatureDate! ; }

    refusedSignature = B.signatures?.find(signature => signature.signatureStatus === SignatureStatus.Refused) ;
    if ($ok(refusedSignature)) { dateB = $ok(refusedSignature!.refusalDate) ? TSDate.fromIsoString(refusedSignature!.refusalDate) : refusedSignature!.signatureDate! ; }
    return $dateorder(dateB, dateA) ;
}

function _compareCanceledSessionDto(A: SessionDto, B: SessionDto): NonNullable<Comparison> {
    let dateA: Nullable<TSDate> = A.validity.end ;
    let dateB: Nullable<TSDate> = B.validity.end ;
    let canceledSignature = A.signatures?.find(signature => signature.signatureStatus === SignatureStatus.Canceled) ;
    if ($ok(canceledSignature)) { dateA = canceledSignature?.signatureDate ; }
    
    canceledSignature = B.signatures?.find(signature => signature.signatureStatus === SignatureStatus.Canceled) ;
    if ($ok(canceledSignature)) { dateB = canceledSignature?.signatureDate ; }
    return $dateorder(dateB, dateA) ;
}

function _compareExpiredSessionDto(A: SessionDto, B: SessionDto): NonNullable<Comparison> {
    let dateA: Nullable<TSDate> = A.validity.end ;
    let dateB: Nullable<TSDate> = B.validity.end ;
    if (A.status === SessionStatus.Refused) {
        let refusedSignature = A.signatures?.find(signature => signature.signatureStatus === SignatureStatus.Refused) ;
        if ($ok(refusedSignature)) { dateA = $ok(refusedSignature!.refusalDate) ? TSDate.fromIsoString(refusedSignature!.refusalDate) : refusedSignature!.signatureDate! ; }
    }
    if (B.status === SessionStatus.Refused) {
        let refusedSignature = B.signatures?.find(signature => signature.signatureStatus === SignatureStatus.Refused) ;
        if ($ok(refusedSignature)) { dateB = $ok(refusedSignature!.refusalDate) ? TSDate.fromIsoString(refusedSignature!.refusalDate) : refusedSignature!.signatureDate! ; }
    }
    if (A.status === SessionStatus.Canceled) {
        let canceledSignature = A.signatures?.find(signature => signature.signatureStatus === SignatureStatus.Canceled) ;
        if ($ok(canceledSignature)) { dateA = canceledSignature?.signatureDate ; }
    }
    if (B.status === SessionStatus.Canceled) {
        let canceledSignature = B.signatures?.find(signature => signature.signatureStatus === SignatureStatus.Canceled) ;
        if ($ok(canceledSignature)) { dateB = canceledSignature?.signatureDate ; }
    }

    return $dateorder(dateB, dateA) ;
}
